/**
 * Тренды: типы ответов `/api/trends*`, `/api/trend-types/*`, `/api/tasks` и
 * мелкие хелперы экранов «Тренды», «Тематика», «Тип».
 *
 * Формы — по живому ответу сервиса (reels/trends.py: topics/topic/type_detail/
 * tasks). Поля, которых у нового типа ещё нет (история Wordstat не собиралась),
 * объявлены `null`-абельными: экран обязан это пережить, а не упасть.
 */
import { apiFetch, ApiFailure } from "@/lib/api";

export type MonthPoint = { month: string; count: number };

/** Сводка по 24 месяцам — `_stats` сервиса. */
export type MonthStats = {
  months: MonthPoint[];
  year: number;
  year_prev: number;
  yoy: number | null;
  last_month: number | null;
  peak_month: string | null;
};

export type TaskStatus = "queued" | "running" | "done" | "failed" | "cancelled";

export type CollectTask = {
  id: number;
  kind: "wordstat" | "pinterest" | "pinterest_batch" | "lens";
  kind_name: string;
  status: TaskStatus;
  status_name: string;
  topic_id: number | null;
  query_id: number | null;
  /** Пакет, породивший задачу (`pinterest_batch`); у одиночных — `null`. */
  parent_id: number | null;
  params: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error: string | null;
  author: string | null;
  date_create: string;
  date_start: string | null;
  date_finish: string | null;
};

/**
 * Владелец тематики: 0 — общая, иначе это подборка сотрудника (CRM userID).
 * Подборку видят все, правит только владелец — сервис отвечает 403 NOT_OWNER.
 */
export type Owned = { owner_user_id: number; owner_name: string | null };

export type TopicRow = Owned & {
  id: number;
  name: string;
  /** Затравка Wordstat; у подборки может не быть. */
  phrase: string | null;
  total: number | null;
  date_collect: string | null;
  types: number;
  phrases: number;
  refs: number;
};

export type TopicList = { topics: TopicRow[]; tasks: CollectTask[] };

export type TypeState = "active" | "ignored";

export type TrendType = MonthStats & {
  id: number;
  grp: string;
  name: string;
  phrase: string;
  /** Запрос для англоязычного Pinterest; `null` — переведётся при сборе. */
  phrase_en: string | null;
  total: number | null;
  state: TypeState;
  date_collect: string | null;
  refs: number;
  date_search: string | null;
  cover: string | null;
};

/**
 * Пакет сбора подборки (`batch_summary`): сам пакет и счётчики его дочерних
 * задач. Прогресс берём отсюда, а не из `tasks` темы: там только 20 новых, и
 * дочерние пакета их вытеснят.
 */
export type BatchSummary = {
  id: number;
  /** `cancelled` — пакет остановили, пока он ещё стоял в очереди (до раскладки на поиски). */
  status: "queued" | "running" | "done" | "failed" | "cancelled";
  error: string | null;
  result: { translated?: number; planned?: number; skipped?: number; untranslated?: number; cost?: number } | null;
  date_create: string;
  total: number;
  done: number;
  failed: number;
  cancelled: number;
  queued: number;
  /** Что ищется сейчас; `null` — ничего (между задачами или пакет закончен). */
  running: { query_id: number; name: string | null; lang: "ru" | "en" } | null;
};

/** Пакет ещё работает: сам не завершён или у него остались дочерние в очереди/работе. */
export const isBatchActive = (b: BatchSummary | null | undefined): boolean =>
  Boolean(b) && (b!.status === "queued" || b!.status === "running" || b!.queued > 0 || b!.running !== null);

export type Phrase = { text: string; count: number; kind: string };

export type Topic = Owned & {
  id: number;
  name: string;
  phrase: string | null;
  note: string | null;
  total: number | null;
  table_from: string | null;
  table_to: string | null;
  date_collect: string | null;
  types: TrendType[];
  phrases: Phrase[];
  associations: Phrase[];
  tasks: CollectTask[];
  /** Последний пакет сбора подборки; `null` — не запускали. */
  batch: BatchSummary | null;
  /** Сколько поисков (тип × язык) ещё не собрано — то, что поставит кнопка пакета. */
  batch_todo: number;
  /** Пинов с картинкой по всем словам без повторов — кнопка «Все референсы · N». */
  refs: number;
};

export type PinScope = "pins" | "videos";

export type PinSearch = {
  id: number;
  text: string;
  scope: PinScope;
  pages: number;
  seen: number;
  downloaded: number;
  author: string | null;
  date_create: string;
};

/**
 * Пин типа. Сохранения и лайки Pinterest считает по КАРТИНКЕ (все её копии), а
 * дата — у копии; репины/пересылки/комментарии и `origin` — из карточки пина,
 * она снята только у «лучших» сбора (иначе `null`). `speed` — репинов этой
 * копии в день, `trend` — во сколько раз быстрее медианы среза (слова или подборки).
 */
export type Pin = {
  id: number;
  code: string;
  kind: "image" | "video";
  origin: "own" | "repin" | null;
  title: string | null;
  saves: number | null;
  reactions: number | null;
  repins: number | null;
  shares: number | null;
  comments: number | null;
  followers: number | null;
  video_ms: number | null;
  link: string | null;
  domain: string | null;
  date_publish: string | null;
  date_detail: string | null;
  age_days: number | null;
  speed: number | null;
  trend: number | null;
  /** Оригинал — есть у «лучших» и у тех, что клали в библиотеку. */
  asset_path: string | null;
  /** Превью 474px — у всех увиденных пинов. */
  thumb_path: string | null;
  asset_width: number | null;
  asset_height: number | null;
  video_path: string | null;
  position: number | null;
  in_libraries: number;
  /** Теги пина (глобальный словарь, `lib/tags.ts`). */
  tag_ids: number[];
  /** Сходство с фото платья (SigLIP) — только в выдаче `/api/dresses/:id/pins`. */
  similarity?: number | null;
  /** Слова (типы), нашедшие пин; на экране подборки их бывает несколько. */
  type_ids?: number[];
};

export type TypeDetail = MonthStats &
  Owned & {
  id: number;
  topic_id: number;
  topic_name: string;
  grp: string;
  name: string;
  phrase: string;
  total: number | null;
  state: TypeState;
  date_collect: string | null;
  searches: PinSearch[];
  pins: Pin[];
  tasks: CollectTask[];
};

/** Слово подборки в ответе «все референсы»: для фильтра и подписи плитки. */
export type TopicWord = { id: number; name: string; grp: string; phrase: string; state: TypeState };

/**
 * Все референсы подборки: пины всех её слов без повторов, потолок по
 * сохранениям (`truncated` — отрезали, `total` — сколько всего). `trend` у пина
 * здесь — к медиане ПОДБОРКИ, а не слова.
 */
export type TopicPins = Owned & {
  id: number;
  name: string;
  phrase: string | null;
  types: TopicWord[];
  pins: Pin[];
  total: number;
  truncated: boolean;
};

/** Может ли вошедший править: общую тематику — любой, подборку — только владелец. */
export const canEdit = (t: Owned, userId: number | undefined): boolean =>
  t.owner_user_id === 0 || t.owner_user_id === userId;

/** Сбор, по которому стоит опрашивать сервис: он ещё не кончился. */
export const isTaskActive = (t: CollectTask): boolean => t.status === "queued" || t.status === "running";

/** Опрос раз в 3 с, пока у экрана есть незавершённый сбор; иначе — тишина. */
export const POLL_MS = 3000;
export const pollWhileActive = (tasks: CollectTask[] | undefined): number | false =>
  tasks?.some(isTaskActive) ? POLL_MS : false;

/**
 * Повтор запроса экрана: отказ 4xx (нет такой тематики, нет входа) повтором не
 * лечится — показываем его сразу, а не после трёх попыток и семи секунд
 * «Загружаю…». Сбой сети и 5xx — повторяем дважды.
 */
export const retryQuery = (count: number, error: unknown): boolean =>
  !(error instanceof ApiFailure && error.status >= 400 && error.status < 500) && count < 2;

/** Ключи кэша react-query — в одном месте, чтобы сброс после записи не промахнулся. */
export const trendKeys = {
  list: ["trends"] as const,
  topic: (id: number) => ["trend-topic", id] as const,
  type: (id: number) => ["trend-type", id] as const,
  topicPins: (id: number) => ["trend-topic-pins", id] as const,
};

export const loadTopics = () => apiFetch<TopicList>("/api/trends");
export const loadTopic = (id: number) => apiFetch<Topic>(`/api/trends/${id}`);
export const loadType = (id: number) => apiFetch<TypeDetail>(`/api/trend-types/${id}`);
export const loadTopicPins = (id: number) => apiFetch<TopicPins>(`/api/trends/${id}/pins`);

export const post = <T>(path: string, body: unknown) =>
  apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) });

const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const MONTHS_FULL = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

/** «2024-10-01» → «окт 24». Строку разбираем руками: `new Date` сдвинул бы месяц поясом. */
export function monthShort(value: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})/.exec(value ?? "");
  if (!m) return "—";
  return `${MONTHS_SHORT[Number(m[2]) - 1] ?? "?"} ${m[1]!.slice(2)}`;
}

/** «2024-10-01» → «октябрь 2024». */
export function monthLong(value: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})/.exec(value ?? "");
  if (!m) return "—";
  return `${MONTHS_FULL[Number(m[2]) - 1] ?? "?"} ${m[1]}`;
}

/** «2026-09-30» → «30.09.2026». */
export function ruDate(value: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "—";
}

/** Год к году со знаком: «+24,3 %», «−2,4 %», «—». */
export function yoyText(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const abs = Math.abs(value).toLocaleString("ru-RU", { maximumFractionDigits: 1 });
  return `${value > 0 ? "+" : value < 0 ? "−" : ""}${abs} %`;
}

/** Рост — зелёный, спад — красный, нет базы — бледный. */
export function yoyTone(value: number | null | undefined): string {
  if (value === null || value === undefined) return "text-fg-faint";
  return value > 0 ? "text-ok" : value < 0 ? "text-err" : "text-fg-muted";
}

/** Значение пикового месяца — из самого ряда, отдельного поля у сервиса нет. */
export function peakCount(stats: MonthStats): number | null {
  if (!stats.peak_month) return null;
  return stats.months.find((m) => m.month === stats.peak_month)?.count ?? null;
}

/** Возраст пина: «сегодня», «5 дн», «7 нед», «4 мес», «2 г». */
export function ageText(days: number | null | undefined): string {
  if (days === null || days === undefined) return "—";
  if (days < 1) return "сегодня";
  if (days < 14) return `${days} дн`;
  if (days < 60) return `${Math.round(days / 7)} нед`;
  if (days < 730) return `${Math.round(days / 30.4)} мес`;
  return `${Math.round(days / 365)} г`;
}

/** Множитель к медиане: 3.24 → «×3,2». */
export function trendText(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `×${value.toLocaleString("ru-RU", { maximumFractionDigits: value < 10 ? 1 : 0 })}`;
}

/** Длительность ролика: 11984 мс → «0:12». */
export function durationText(ms: number | null | undefined): string {
  if (!ms) return "—";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Ссылка на пин — только у настоящего пина: код из цифр. У прочих кодов адреса нет. */
export function pinterestUrl(code: string): string | null {
  return /^\d+$/.test(code) ? `https://ru.pinterest.com/pin/${code}/` : null;
}

/** Фразы сравниваем так же, как их нормализует сервис: нижний регистр, один пробел. */
export const normPhrase = (text: string): string => text.toLowerCase().split(/\s+/).filter(Boolean).join(" ");

/**
 * Название типа по фразе: фраза минус слова затравки. «кружевное свадебное
 * платье» в тематике «свадебное платье» → «кружевное». Если вычесть нечего или
 * не осталось ничего — фраза целиком: это только подсказка в поле, её правят.
 */
export function suggestTypeName(phrase: string, topicPhrase: string | null): string {
  const seed = new Set(normPhrase(topicPhrase ?? "").split(" "));
  const rest = normPhrase(phrase)
    .split(" ")
    .filter((w) => !seed.has(w));
  return rest.length ? rest.join(" ") : normPhrase(phrase);
}
